import MapboxDraw, {
  type DrawCustomMode,
  type DrawCustomModeThis,
  type DrawFeature,
  type DrawLineString,
  type DrawPoint,
  type MapMouseEvent,
} from '@mapbox/mapbox-gl-draw';
import type { Feature, Point } from 'geojson';
import { cloneDeep } from 'lodash';
import type { Map as MapboxMap } from 'mapbox-gl';
import { constrainFeatureMovement } from './utils';

interface State {
  featureId: string;
  selectedCoordPaths: Array<string>;
  feature: DrawFeature;
}
type Delta = { lat: number; lng: number };
type SelectedCoordinates = Array<{ coord_path: string; feature_id: string }>;
export interface LineStringCoordinate {
  index: number;
  lineStringId: string;
  lat: number;
  lng: number;
}

type This = DrawCustomModeThis & DirectSelectModeType;
interface DirectSelectModeType extends DrawCustomMode<State> {
  isDrawPoint: (feature: DrawFeature) => feature is DrawPoint;
  isDrawLineString: (feature: DrawFeature) => feature is DrawLineString;
  dragVertex: (this: This, state: State, e: MapMouseEvent, delta: Delta) => void;
  onVertex: (this: This, state: State, e: MapMouseEvent) => void;
  startDragging: (this: This, state: State, e: MapMouseEvent) => void;
  onMidpoint: (this: This, state: State & { feature: DrawLineString }, e: MapMouseEvent) => void;
  fireUpdate: (
    this: This,
    opts?: {
      coordinateAction?: 'add' | 'delete' | 'move';
      coordinates?: Array<LineStringCoordinate>;
    },
  ) => void;
  pathsToCoordinates: (
    this: This,
    featureId: State['featureId'],
    coordPath: State['selectedCoordPaths'],
  ) => SelectedCoordinates;
  getSelected: () => Array<DrawFeature>;
  onTrash: (this: This, state: State) => void;
  fireActionable: (this: This, state: State) => void;
  deleteFeature: (this: This, featureId: string | Array<string>) => void;
  map: MapboxMap;
}

export const DirectSelectMode = cloneDeep(MapboxDraw.modes.direct_select) as DirectSelectModeType;

DirectSelectMode.dragVertex = (state, _e, delta) => {
  const selectedCoords = state.selectedCoordPaths.map((coord_path) =>
    state.feature.getCoordinate(coord_path),
  );
  const selectedCoordPoints: Array<Feature<Point>> = selectedCoords.map((coords) => ({
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Point',
      coordinates: coords,
    },
  }));

  const constrainedDelta = constrainFeatureMovement(selectedCoordPoints, delta);
  for (let i = 0; i < selectedCoords.length; i++) {
    const coord = selectedCoords[i];
    state.feature.updateCoordinate(
      state.selectedCoordPaths[i],
      coord[0] + constrainedDelta.lng,
      coord[1] + constrainedDelta.lat,
    );
  }
};

DirectSelectMode.onVertex = function (state, e) {
  this.startDragging(state, e);
  const about = e.featureTarget?.properties;
  if (about == null) return;

  const selectedIndex = state.selectedCoordPaths.indexOf(about.coord_path);
  if (!isShiftDown(e) && selectedIndex === -1) {
    state.selectedCoordPaths = [about.coord_path];
  } else if (isShiftDown(e) && selectedIndex === -1) {
    state.selectedCoordPaths.push(about.coord_path);
  }

  const selectedCoordinates = this.pathsToCoordinates(state.featureId, state.selectedCoordPaths);
  this.setSelectedCoordinates(selectedCoordinates);
};

DirectSelectMode.onMidpoint = function (state, e) {
  this.startDragging(state, e);
  const about = e.featureTarget?.properties;
  if (about == null) return;

  state.feature.addCoordinate(about.coord_path, about.lng, about.lat);
  this.fireUpdate({
    coordinateAction: 'add',
    coordinates: [
      {
        lineStringId: about.parent,
        index: Number.parseInt(about.coord_path, 10),
        lng: about.lng,
        lat: about.lat,
      },
    ],
  });
  state.selectedCoordPaths = [about.coord_path];
};

DirectSelectMode.fireUpdate = function (opts = { coordinateAction: 'move' }) {
  this.map.fire('draw.update', {
    action: 'change_coordinates',
    features: this.getSelected().map((f) => f.toGeoJSON()),
    ...opts,
  });
};

DirectSelectMode.onTrash = function (state) {
  // Uses number-aware sorting to make sure '9' < '10'. Comparison is reversed because we want them
  // in reverse order so that we can remove by index safely.
  const selectedCoordinates: Array<LineStringCoordinate> = state.selectedCoordPaths.map((id) => {
    const coordinate = state.feature.getCoordinate(id);

    return {
      index: Number.parseInt(id, 10),
      lng: coordinate[0],
      lat: coordinate[1],
      lineStringId: state.feature.id.toString(),
    };
  });
  state.selectedCoordPaths
    .sort((a, b) => b.localeCompare(a, 'en', { numeric: true }))
    .forEach((id) => this.isDrawLineString(state.feature) && state.feature.removeCoordinate(id));
  this.fireUpdate({
    coordinateAction: 'delete',
    coordinates: selectedCoordinates,
  });
  state.selectedCoordPaths = [];
  this.clearSelectedCoordinates();
  this.fireActionable(state);
  if (state.feature.isValid() === false) {
    this.deleteFeature([state.featureId]);
    this.changeMode('simple_select', {});
  }
};

DirectSelectMode.isDrawPoint = (
  feature?: MapboxDraw.DrawFeature,
): feature is MapboxDraw.DrawPoint =>
  feature?.type === 'Point' || feature?.properties?.['meta:type'] === 'Point';

DirectSelectMode.isDrawLineString = (feature?: MapboxDraw.DrawFeature): feature is DrawLineString =>
  feature?.type === 'LineString' || feature?.properties?.['meta:type'] === 'LineString';

function isShiftDown(e: MapboxDraw.MapMouseEvent): boolean {
  if (!e.originalEvent) return false;
  return e.originalEvent.shiftKey === true;
}
